// domain/constants/currency.js

/**
 * The currency a charge is stated in, and how many minor units make one major unit.
 *
 * A CHARGE IS STORED IN MINOR UNITS — 12000, not 120.00 — and this vocabulary is what makes that
 * number readable. Binary floats cannot represent most decimal fractions, so `0.1 + 0.2` is not
 * `0.3` and `120.5 * 100` is not `12050`. A charge lands on a claim as a legal figure, so the
 * arithmetic has to be exact, and integers are the only representation that is.
 *
 * THE EXPONENT IS PER MEMBER RATHER THAN A CONSTANT 2. Every currency here happens to have two
 * decimal places today, which is exactly why the exponent is worth stating: a zero-decimal currency
 * (JPY, KRW) or a three-decimal one (KWD, BHD) would otherwise arrive as a code change in every
 * place that assumed a hundred. Stated per member, it is a data change.
 *
 * WHICH CURRENCY A CLINIC USES IS NOT DERIVED FROM ITS REGION, deliberately, even though the regions
 * are separate projects and each has an obvious default today. The value is written onto the sheet
 * when it is created, so a stored charge says what it means without anyone having to know which
 * database it came out of. A figure that needs external context to be read is a figure that will
 * eventually be read wrong.
 *
 * @domainObject value-object
 * @invariant Extension is additive. A new currency is a new member with its own exponent, never a
 *   reshape of charges already stored under an existing one.
 * @invariant The exponent is a property of the currency, never of a screen or a locale. Display
 *   grouping and symbol placement are presentation; the number of decimal places a currency HAS is
 *   not.
 * @typedef {'USD' | 'EUR' | 'GBP' | 'DKK' | 'SEK' | 'NOK' | 'CAD' | 'AUD'} Currency
 */
export const CURRENCY = {
  USD: 'USD',
  EUR: 'EUR',
  GBP: 'GBP',
  DKK: 'DKK',
  SEK: 'SEK',
  NOK: 'NOK',
  CAD: 'CAD',
  AUD: 'AUD',
};

/** How many decimal places each currency has. See the docblock for why this is not a constant 2. */
export const CURRENCY_MINOR_UNIT_EXPONENT = {
  [CURRENCY.USD]: 2,
  [CURRENCY.EUR]: 2,
  [CURRENCY.GBP]: 2,
  [CURRENCY.DKK]: 2,
  [CURRENCY.SEK]: 2,
  [CURRENCY.NOK]: 2,
  [CURRENCY.CAD]: 2,
  [CURRENCY.AUD]: 2,
};

export const CURRENCY_VALUES = Object.values(CURRENCY);

export const isCurrency = (value) => CURRENCY_VALUES.includes(value);

/**
 * What to PREFILL when a clinic creates its very first sheet, by deployment region.
 *
 * A SEED, NEVER A RESOLUTION, and the distinction is the whole reason this is safe to have. It is
 * read once, at the moment a charge master is created, and the chosen value is then written onto the
 * sheet and read from there forever after. Nothing looks a currency up by region at read time — a
 * stored charge says what it is denominated in, which is what stops the figure needing external
 * context to be understood.
 *
 * That also means changing an entry here cannot reinterpret a single existing charge. It changes
 * only what the next new charge master starts out suggesting.
 *
 * THE EU ENTRY IS A GUESS, AND TODAY AN UNCORRECTABLE ONE. That project serves several currencies, so
 * `EUR` is wrong for a clinic billing in DKK, SEK or NOK — and there is no control on the editor to
 * change it, while `CurrencyChangeRejected` refuses to change it afterwards. So a Danish clinic's first
 * save locks its charge master to EUR for good. The seed is only safe to the extent the region has one
 * obvious currency, which is true of `us`, `ca` and `au` and not of `eu`. Making the currency an
 * explicit choice on the first sheet is the fix; until then this is a known limitation rather than a
 * defensible default.
 *
 * Read the LIVE region, never `defaultRegion` from `boot/firebase` — that is a build-time fallback
 * ('us' in production, 'eu' otherwise), not the region the session is connected to.
 */
export const DEFAULT_CURRENCY_BY_REGION = {
  us: CURRENCY.USD,
  ca: CURRENCY.CAD,
  au: CURRENCY.AUD,
  eu: CURRENCY.EUR,
};

/**
 * Symbols we are willing to show INSTEAD of the ISO code.
 *
 * DELIBERATELY SHORT, and everything absent falls back to its code. A symbol is only clearer than a
 * code when it names exactly one currency: `$` is used by the US, Canada, Australia and a dozen
 * others, so showing it to a Canadian clinic would say less than `CAD` does. `$` maps to USD anyway
 * as the one deliberate exception — it is what a US biller expects on a price list.
 *
 * `HrCurrencyField` holds its own copy of this, because the design system cannot import from a
 * bounded context. The duplication is structural rather than accidental — the same shape as the
 * unit-basis vocabulary duplicated across repos — so a change here needs the same change there.
 */
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  INR: '₹',
  KRW: '₩',
};

/**
 * How a currency is written next to an amount: its symbol where that is unambiguous, else its code.
 *
 * @param {string|null|undefined} currency
 * @returns {string|null} null when there is no currency to state
 */
export function currencyAffix(currency) {
  if (typeof currency !== 'string' || currency.length === 0) {
    return null;
  }
  return CURRENCY_SYMBOLS[currency] ?? currency;
} // currencyAffix()

/**
 * How many decimal places this currency has, or null if we do not know the currency.
 *
 * Null rather than a default of 2: guessing the exponent of a currency we have never heard of is how
 * a three-decimal charge silently loses a digit.
 *
 * @param {string} currency
 * @returns {number|null}
 */
export function minorUnitExponent(currency) {
  return CURRENCY_MINOR_UNIT_EXPONENT[currency] ?? null;
} // minorUnitExponent()

/**
 * Is this a value a charge may be stored as — a non-negative SAFE integer of minor units?
 *
 * SAFE, NOT MERELY INTEGER, and the difference is not academic. `Number.isInteger(1e20)` is true, but
 * that value has already lost its last digits to the float's precision: it can neither be read back
 * as the amount somebody typed nor compared reliably against another. A charge that cannot survive a
 * round trip is not a charge, so it resolves to no charge and holds at billing.
 *
 * NEGATIVE IS REFUSED for the same reason `toMinorUnits` refuses a sign. A clinic does not bill a
 * negative amount, so a stored one is a value this system never wrote, and treating it as a charge
 * would put it on a claim.
 *
 * ONE PREDICATE, USED EVERYWHERE A CHARGE VALUE IS JUDGED — the mapper, the resolver, the presenter
 * and the editor's save guard. Four copies of the same expression is how one of them ends up
 * disagreeing, and the one that disagrees decides whether something bills.
 *
 * @param {*} chargeMinor
 * @returns {boolean}
 */
export function isChargeMinorUnits(chargeMinor) {
  return Number.isSafeInteger(chargeMinor) && chargeMinor >= 0;
} // isChargeMinorUnits()

/**
 * A typed amount → minor units, or null if it is not a charge this currency can hold.
 *
 * PARSED AS TEXT, NEVER MULTIPLIED. `120.5 * 100` evaluates to `12050.000000000002`, and rounding
 * that away works until the day it does not. Splitting the string on its decimal point and padding
 * the fraction to the currency's exponent is exact for every input, which is the only standard money
 * arithmetic can be held to.
 *
 * MORE PRECISION THAN THE CURRENCY HAS IS REFUSED, not rounded. `120.999` in a two-decimal currency
 * is not a charge anyone can bill, and quietly turning it into `121.00` changes the number somebody
 * typed into a claim. The field restricts what can be entered; this refuses what gets past it.
 *
 * A TRAILING DECIMAL POINT IS ACCEPTED, and `'42.'` means 42. It is not a sloppy value to be
 * tolerated — it is what every amount looks like for the keystroke between the point and the first
 * decimal. Refusing it made a charge vanish mid-typing: the field showed "needs a charge" and lost
 * its clear control while the user was still writing the number. Nothing is ambiguous about it, so
 * there is nothing to refuse.
 *
 * ZERO IS A VALID CHARGE and is not the same as an absent one. A line charged at zero says the
 * clinic bills this code at no cost; a line with no charge says nobody has decided yet and it holds
 * at billing. Collapsing the two would delete a decision.
 *
 * @param {string|number} amount - as typed, e.g. '120.50'. Grouping separators are not accepted.
 * @param {string} currency
 * @returns {number|null} integer minor units, or null when the input is not a usable charge
 */
export function toMinorUnits(amount, currency) {
  const exponent = minorUnitExponent(currency);

  if (exponent === null) {
    return null;
  }
  const text = `${amount ?? ''}`.trim();

  // Digits, optionally a single decimal point and any digits after it — including none, so a
  // half-typed '42.' is read as 42. No sign: a negative charge is not a thing a clinic bills, and
  // accepting one here would push the question to whoever reads it later.
  if (!/^\d+(\.\d*)?$/.test(text)) {
    return null;
  }
  const [whole, fraction = ''] = text.split('.');

  if (fraction.length > exponent) {
    return null;
  }
  // REFUSED RATHER THAN ROUNDED, on the same terms as excess precision above. An amount past the safe
  // integer range comes back from `Number` already altered — the field would then show a figure
  // nobody typed, and the sheet would store it as if they had.
  const minorUnits = Number(`${whole}${fraction.padEnd(exponent, '0')}`);

  return isChargeMinorUnits(minorUnits) ? minorUnits : null;
} // toMinorUnits()

/**
 * Minor units → the major-unit string a field shows, e.g. `12050` → `'120.50'`.
 *
 * RETURNS A STRING rather than a number, because the trailing zero in `120.50` is part of how a
 * charge is written and a number cannot carry it. The caller adds a symbol and grouping; that is
 * locale, and this module holds none.
 *
 * @param {number|null|undefined} chargeMinor
 * @param {string} currency
 * @returns {string|null} null when there is no charge, or the currency is unknown
 */
export function fromMinorUnits(chargeMinor, currency) {
  const exponent = minorUnitExponent(currency);

  if (exponent === null || !isChargeMinorUnits(chargeMinor)) {
    return null;
  }
  if (exponent === 0) {
    return `${chargeMinor}`;
  }
  const digits = `${chargeMinor}`.padStart(exponent + 1, '0');

  return `${digits.slice(0, -exponent)}.${digits.slice(-exponent)}`;
} // fromMinorUnits()
